import { useState, useEffect } from "react"
import FSTPSelect from "../../Components/FSTP/FSTPSelect"
import BasicInfoSection from "../../Components/FSTP/BasicInfoSection"
import TechnicalInfoSection from "../../Components/FSTP/TechnicalInfoSection"
import { fstpData } from "../../Components/FSTP/fstpData"
import { useTranslation } from "react-i18next";



const FSTPDashboard = () => {
  const { t } = useTranslation();

  const [fstpList, setFstpList] = useState([])
  const [selectedId, setSelectedId] = useState(null)

  const formatDryingBed = (dryingBed) => {
    if (!dryingBed) return "—";

    const parts = [];

    // Unplanted beds
    if (dryingBed.unplanted) {
      const u = dryingBed.unplanted;

      if (u.count && u.size) {
        parts.push(
          `Unplanted: ${u.count} beds (${u.size.length || "?"} × ${u.size.width || "?"}${u.size.depth ? ` × ${u.size.depth}` : ""
          })`
        );
      } else if (u.count && u.capacityPerBed) {
        // For ABR-style or capacity per bed
        parts.push(`Unplanted: ${u.count} beds (Capacity per bed: ${u.capacityPerBed || "—"})`);
      } else {
        parts.push("Unplanted: —");
      }
    }

    // Planted beds
    if (dryingBed.planted) {
      const p = dryingBed.planted;
      if (p.count && p.size) {
        parts.push(
          `Planted: ${p.count} beds (${p.size.length || "?"} × ${p.size.width || "?"}${p.size.depth ? ` × ${p.size.depth}` : ""
          })`
        );
      } else if (p.count && p.size?.areaPerBed) {
        parts.push(`Planted: ${p.count} beds (Area per bed: ${p.size.areaPerBed || "—"})`);
      } else {
        parts.push("Planted: —");
      }
    }

    // Total / Other formats
    if (dryingBed.total) {
      const t = dryingBed.total;
      parts.push(`Total: ${t.count || "—"} beds (Area: ${t.size?.area || "—"})`);
    }

    // fallback for string
    if (typeof dryingBed === "string") return dryingBed;

    return parts.join(" | ");
  };



  useEffect(() => {
    const mapped = fstpData.map((item) => ({
      id: item.id,
      title: item.basicInfo.name,

      basicInfo: {
        title: item.basicInfo.name,
        technology: item.design?.wastewaterTreatment || "—",
        administrativeDivision: item.basicInfo.administrativeDivision,
        areaName: item.basicInfo.areaName,
        assetOwner: "Government of Bangladesh",
        assetOwnerLogo: "/logos/govt.png",
        organization: item.basicInfo.organization,
        organizationLogo: item.basicInfo.organizationLogo || null,
        projectName: item.basicInfo.projectName || "—"
      },

      technicalInfo: {
        title: `${t("fstp_page.technicalInfo.techTitle")} | ${item.basicInfo.name}`,
        data: [
          {
            left: { label: t("fstp_page.technicalInfo.installationYear"), value: item.basicInfo.installationYear || "—" },
            right: { label: t("fstp_page.technicalInfo.endUserServices"), value: item.endUser.served || "—" }
          },
          {
            left: {
              label: t("fstp_page.technicalInfo.costInfo"),
              additionalInfo: [
                { label: t("fstp_page.technicalInfo.capEx"), value: item.costInfo?.capEx || "—" },
                { label: t("fstp_page.technicalInfo.opEx"), value: item.costInfo?.opEx || "—" },
                { label: t("fstp_page.technicalInfo.landCost"), value: item.costInfo?.landCost || "—" },
              ],
            },
            right: {
              label: t("fstp_page.technicalInfo.capacity"),
              additionalInfo: [
                { label: t("fstp_page.technicalInfo.designTreatmentCapacity"), value: item.capacity?.designedTreatmentCapacity || "—" },
                { label: t("fstp_page.technicalInfo.actualTreatmentCapacity"), value: item.capacity?.actualTreatmentCapacity || "—" },
                { label: t("fstp_page.technicalInfo.energyRequirement"), value: item.capacity?.designedEnergyRequirementsActualUsage || "—" },
              ],
            },
          },

          {
            left: {
              label: t("fstp_page.technicalInfo.receivedSSC"),
              additionalInfo: [
                { label: t("fstp_page.technicalInfo.totalSolids"), value: item.sludgeCharacteristics?.totalSolids || "—" },

                { label: t("fstp_page.technicalInfo.fecalColiform"), value: item.sludgeCharacteristics?.fecalColiform || "—" },
                { label: t("fstp_page.technicalInfo.bod"), value: item.sludgeCharacteristics?.BOD || "—" },
                { label: t("fstp_page.technicalInfo.cod"), value: item.sludgeCharacteristics?.COD || "—" },
                { label: t("fstp_page.technicalInfo.nutrients"), value: item.sludgeCharacteristics?.NO3 || "—" },
                { label: t("fstp_page.technicalInfo.potassium"), value: item.sludgeCharacteristics?.PO4 || "—" },
                { label: t("fstp_page.technicalInfo.tts"), value: item.sludgeCharacteristics?.TSS || "—" },
              ],
            },
            right: {
              label: t("fstp_page.technicalInfo.treatmentLevel"),
              additionalInfo: [
                { label: t("fstp_page.technicalInfo.totalSolids"), value: item.effluentStandard?.totalSolids || "—" },

                { label: t("fstp_page.technicalInfo.fecalColiform"), value: item.effluentStandard?.fecalColiform || "—" },
                { label: t("fstp_page.technicalInfo.bod"), value: item.effluentStandard?.BOD || "—" },
                { label: t("fstp_page.technicalInfo.cod"), value: item.effluentStandard?.COD || "—" },
                { label: t("fstp_page.technicalInfo.nutrients"), value: item.effluentStandard?.NO3 || "—" },
                { label: t("fstp_page.technicalInfo.potassium"), value: item.effluentStandard?.PO4 || "—" },
                { label: t("fstp_page.technicalInfo.tts"), value: item.effluentStandard?.TSS || "—" },
              ],
            },
          },

          {
            left: {
              label: t("fstp_page.technicalInfo.onSiteContainment"),
              additionalInfo: (() => {
                const { septicTank, singlePit, doublePit, pitLatrine, others } = item.containmentSystem || {};

                const hasPitLatrine = !!pitLatrine;
                const hasSingleOrDoublePit = !!singlePit || !!doublePit;

                const info = [
                  { label: t("fstp_page.technicalInfo.septicTank"), value: septicTank || "—" },

                  // Show Single/Double Pit only if Pit Latrine is not present
                  !hasPitLatrine && { label: t("fstp_page.technicalInfo.singlePit"), value: singlePit || "—" },
                  !hasPitLatrine && { label: t("fstp_page.technicalInfo.doublePit"), value: doublePit || "—" },

                  // Show Pit Latrine only if Single/Double Pit are not present
                  !hasSingleOrDoublePit && { label: t("fstp_page.technicalInfo.pitLatrine"), value: pitLatrine || "—" },

                  { label: t("fstp_page.technicalInfo.other"), value: others || "—" },
                ].filter(Boolean);

                return info;
              })(),
            },
            right: {
              label: t("fstp_page.technicalInfo.plantDetails"),
              additionalInfo: [
                { label: t("fstp_page.technicalInfo.footPrint"), value: item.plantDetails?.footprint || "—" },
                { label: t("fstp_page.technicalInfo.collectionUnit"), value: item.plantDetails?.collectionUnit || "—" },
                { label: t("fstp_page.technicalInfo.emptFee"), value: item.plantDetails?.emptyingFee || "—" },
                { label: t("fstp_page.technicalInfo.tripFee"), value: item.plantDetails?.tippingFee || "—" },
              ],
            },
          },

          {
            left: {
              label: t("fstp_page.technicalInfo.designCriteria"),
              additionalInfo: [
                { label: t("fstp_page.technicalInfo.landArea"), value: item.design?.criteria?.landArea || "—" },
                { label: t("fstp_page.technicalInfo.dryingBedArea"), value: formatDryingBed(item.design?.criteria?.dryingBed) },
              ],
            },
            right: { label: t("fstp_page.technicalInfo.wasteWaterTreatment"), value: item.design?.wastewaterTreatment || "—" },

          },
        ]
      }
    }))

    setFstpList(mapped)
    setSelectedId(mapped[0]?.id)
  }, [t])

  const selectedFSTP = fstpList.find((f) => f.id === selectedId)

  if (!selectedFSTP) return <div className="p-8 text-gray-600">Loading...</div>

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <FSTPSelect options={fstpList} value={selectedId} onChange={setSelectedId} />
        <BasicInfoSection basicInfo={selectedFSTP.basicInfo} />
        <TechnicalInfoSection technicalInfo={selectedFSTP.technicalInfo} />
      </div>
    </div>
  )
}
export default FSTPDashboard;
